import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { TrendingUp, Activity, Clock, BarChart3, Loader2, RefreshCw, AlertCircle, Search, X } from "lucide-react";
import StrategyIdeas from "@/components/StrategyIdeas";
import { OIBuildupCard, GreeksCard, FiiDiiCard, PremarketCard, StrikeSelectCard } from "@/components/OptionsQuadrants";
import { useAuth } from "@/context/AuthContext";

const QUICK_CHIPS = ["NIFTY", "BANKNIFTY", "RELIANCE", "HDFCBANK", "ICICIBANK", "TCS"];

function fmt(n, opts = {}) {
  if (n === null || n === undefined) return "—";
  const { dec = 2, suffix = "" } = opts;
  if (typeof n !== "number") return String(n);
  return n.toLocaleString("en-IN", { minimumFractionDigits: dec, maximumFractionDigits: dec }) + suffix;
}

function fmtCompact(n) {
  if (n === null || n === undefined) return "—";
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
}

function StatCard({ label, value, sublabel, accent, testid }) {
  return (
    <div className="border border-zinc-800 bg-[#0a0a0a] p-4" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">{label}</div>
      <div className={`font-heading text-2xl md:text-3xl font-black tracking-tight ${accent || "text-white"}`}>{value}</div>
      {sublabel && <div className="text-[10px] font-mono text-zinc-500 mt-1 uppercase tracking-wide leading-tight">{sublabel}</div>}
    </div>
  );
}

/**
 * Searchable F&O symbol picker — replaces the old fixed-chip selector.
 * Indian retail traders trade ~70 stock options + 6 indices. The picker
 * autocompletes by prefix on symbol OR substring on company name.
 */
function SymbolPicker({ value, onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null); // currently selected metadata
  const [searching, setSearching] = useState(false);
  const wrapperRef = useRef(null);

  // Load metadata for the active symbol so we can render its full name.
  useEffect(() => {
    if (!value) return;
    api.get(`/options-lab/search?q=${encodeURIComponent(value)}&limit=5`).then(({ data }) => {
      const hit = data.results?.find((r) => r.symbol === value);
      setActive(hit || { symbol: value, name: value, category: "Stock", lot_size: 0 });
    }).catch(() => setActive({ symbol: value, name: value, category: "Stock", lot_size: 0 }));
  }, [value]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/options-lab/search?q=${encodeURIComponent(query)}&limit=15`);
        setResults(data.results || []);
      } catch (e) {
        console.warn("options-lab symbol search failed:", e?.message || e);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(id);
  }, [query, open]);

  // Click-outside.
  useEffect(() => {
    function onDoc(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (sym) => {
    setOpen(false);
    setQuery("");
    onSelect(sym);
  };

  return (
    <div ref={wrapperRef} className="relative w-full md:w-[380px]" data-testid="options-lab-symbol-picker">
      {/* Trigger */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 border border-zinc-800 hover:border-[#007AFF]/60 bg-[#0a0a0a] px-3 py-2 text-left"
        data-testid="options-lab-picker-trigger"
      >
        <Search className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg font-black uppercase tracking-tight text-white leading-none">{value}</div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono truncate mt-0.5">
            {active ? `${active.category} · ${active.name} · Lot ${active.lot_size}` : "Choose underlying"}
          </div>
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 border border-zinc-700 bg-[#0a0a0a] shadow-2xl">
          <div className="relative border-b border-zinc-800">
            <Search className="h-3.5 w-3.5 absolute top-1/2 -translate-y-1/2 left-3 text-zinc-500" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search index or stock — e.g. RELIANCE, BANK, ICICI…"
              className="w-full bg-transparent pl-9 pr-9 py-2.5 text-xs text-white placeholder:text-zinc-600 focus:outline-none font-mono"
              data-testid="options-lab-picker-search"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute top-1/2 -translate-y-1/2 right-3 text-zinc-600 hover:text-zinc-300">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {searching && (
              <div className="px-4 py-6 text-center text-[10px] uppercase tracking-widest font-mono text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 inline animate-spin mr-2" /> Searching…
              </div>
            )}
            {!searching && results.length === 0 && (
              <div className="px-4 py-6 text-center text-[10px] uppercase tracking-widest font-mono text-zinc-600">
                No F&amp;O underlying matched
              </div>
            )}
            {!searching && results.map((r) => (
              <button
                key={r.symbol}
                onClick={() => pick(r.symbol)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[#007AFF]/5 border-b border-zinc-900/40 last:border-b-0 ${r.symbol === value ? "bg-[#007AFF]/10" : ""}`}
                data-testid={`options-lab-picker-result-${r.symbol}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-white font-bold">{r.symbol}</span>
                    <span className={`text-[8px] uppercase tracking-widest font-mono px-1.5 py-0.5 ${r.category === "Index" ? "bg-[#007AFF]/15 text-[#007AFF]" : "bg-zinc-800 text-zinc-400"}`}>
                      {r.category}
                    </span>
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate mt-0.5">{r.name}</div>
                </div>
                <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500 flex-shrink-0">
                  Lot {r.lot_size}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OptionsLabPage() {
  const { user } = useAuth();
  const [symbol, setSymbol] = useState("NIFTY");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expiry, setExpiry] = useState("");

  const load = useCallback(async (sym, exp = "") => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ symbol: sym });
      if (exp) params.set("expiry", exp);
      const { data } = await api.get(`/options-lab/header-stats?${params.toString()}`);
      setStats(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Failed to load options data";
      setError(msg);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(symbol, expiry);
    // Auto-refresh every 60s.
    const id = setInterval(() => load(symbol, expiry), 60_000);
    return () => clearInterval(id);
  }, [symbol, expiry, load]);

  const pcrTone = stats?.pcr >= 1.3 ? "text-[#22C55E]" : stats?.pcr <= 0.7 ? "text-[#EF4444]" : "text-[#F59E0B]";
  const vixTone = stats?.india_vix >= 18 ? "text-[#EF4444]" : stats?.india_vix <= 11 ? "text-[#F59E0B]" : "text-[#22C55E]";
  const dteTone = stats?.dte <= 1 ? "text-[#EF4444]" : stats?.dte <= 3 ? "text-[#F59E0B]" : "text-white";

  return (
    <div className="space-y-6" data-testid="options-lab-page">
      {/* Header strip */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Pro · Options Intelligence</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">OPTIONS LAB</h1>
          <div className="text-xs text-zinc-500 mt-1">Institutional metrics on the underlying you trade.</div>
        </div>

        <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
          <SymbolPicker
            value={symbol}
            onSelect={(s) => { setSymbol(s); setExpiry(""); }}
          />
          <button
            onClick={() => load(symbol, expiry)}
            disabled={loading}
            className="px-2.5 py-1.5 border border-zinc-800 hover:border-zinc-600 text-zinc-500 hover:text-zinc-200 disabled:opacity-50"
            data-testid="options-lab-refresh"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Quick chips — the 6 most popular underlyings for fast hops */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] uppercase tracking-widest font-mono text-zinc-600 mr-1">Quick</span>
        {QUICK_CHIPS.map((s) => (
          <button
            key={s}
            onClick={() => { setSymbol(s); setExpiry(""); }}
            className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest border transition-colors ${symbol === s ? "border-[#007AFF] bg-[#007AFF]/10 text-[#007AFF]" : "border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-200"}`}
            data-testid={`options-lab-quick-${s.toLowerCase()}`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Expiry chips */}
      {stats?.expiries && stats.expiries.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mr-1">Expiry</span>
          {stats.expiries.map((e) => (
            <button
              key={e.expiry}
              onClick={() => setExpiry(e.expiry)}
              className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest border transition-colors ${expiry === e.expiry || (!expiry && e === stats.expiries[0]) ? "border-[#007AFF] text-[#007AFF]" : "border-zinc-800 text-zinc-500 hover:text-zinc-200"}`}
              data-testid={`options-lab-expiry-${e.expiry}`}
            >
              {e.date}
            </button>
          ))}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4 flex items-start gap-3" data-testid="options-lab-error">
          <AlertCircle className="h-4 w-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-xs uppercase tracking-widest font-mono font-bold text-[#F59E0B]">Options data unavailable</div>
            <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{error}</div>
            {user?.is_admin && (error.toLowerCase().includes("data api") || error.toLowerCase().includes("token")) && (
              <div className="text-[10px] text-zinc-500 mt-2 font-mono uppercase tracking-widest">
                · Admin → Data Feeds → Rotate Token to refresh the DHAN data feed.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-zinc-800">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="border border-zinc-800 bg-[#0a0a0a] p-4 h-24 animate-pulse" />
          ))}
        </div>
      )}

      {/* Header stat row */}
      {stats && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-zinc-800">
            <StatCard
              label="Spot"
              value={fmt(stats.spot, { dec: 2 })}
              sublabel={`ATM ${fmt(stats.atm_strike, { dec: 0 })}`}
              testid="opt-stat-spot"
            />
            <StatCard
              label="PCR (OI)"
              value={fmt(stats.pcr, { dec: 2 })}
              sublabel={stats.pcr_interpretation}
              accent={pcrTone}
              testid="opt-stat-pcr"
            />
            <StatCard
              label="Max Pain"
              value={fmt(stats.max_pain, { dec: 0 })}
              sublabel={stats.max_pain_gap_pct !== null && stats.max_pain_gap_pct !== undefined
                ? `${stats.max_pain_gap_pct >= 0 ? "+" : ""}${stats.max_pain_gap_pct}% from spot`
                : null}
              accent={Math.abs(stats.max_pain_gap_pct || 0) < 0.3 ? "text-[#F59E0B]" : "text-white"}
              testid="opt-stat-maxpain"
            />
            <StatCard
              label="ATM IV"
              value={stats.atm_iv ? `${fmt(stats.atm_iv, { dec: 1 })}%` : "—"}
              sublabel={stats.ce_iv && stats.pe_iv ? `CE ${fmt(stats.ce_iv, { dec: 1 })} · PE ${fmt(stats.pe_iv, { dec: 1 })}` : null}
              testid="opt-stat-iv"
            />
            <StatCard
              label="DTE"
              value={stats.dte !== null && stats.dte !== undefined ? `${stats.dte}d` : "—"}
              sublabel={stats.dte === 0 ? "EXPIRY TODAY · theta-burn" : stats.dte === 1 ? "Expires tomorrow" : null}
              accent={dteTone}
              testid="opt-stat-dte"
            />
            <StatCard
              label="India VIX"
              value={fmt(stats.india_vix, { dec: 2 })}
              sublabel={stats.india_vix >= 18 ? "High · expensive premiums" : stats.india_vix <= 11 ? "Low · cheap but dead" : "Normal regime"}
              accent={vixTone}
              testid="opt-stat-vix"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800">
            <StatCard label="Call OI" value={fmtCompact(stats.call_oi_total)} testid="opt-stat-call-oi" />
            <StatCard label="Put OI" value={fmtCompact(stats.put_oi_total)} testid="opt-stat-put-oi" />
            <StatCard label="Total OI" value={fmtCompact(stats.total_oi)} testid="opt-stat-total-oi" />
            <StatCard label="Total Volume" value={fmtCompact(stats.total_volume)} testid="opt-stat-total-vol" />
          </div>

          {/* Strategy Ideas — full-width hero section under header stats */}
          <StrategyIdeas symbol={symbol} expiry={expiry} />

          {/* Strike selection — same intelligence as the Dashboard Best-Strike strip */}
          <StrikeSelectCard symbol={symbol} />

          {/* Four-quadrant deep-dive grid — Sprint 3: all live */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OIBuildupCard symbol={symbol} expiry={expiry} />
            <GreeksCard symbol={symbol} expiry={expiry} />
            <FiiDiiCard />
            <PremarketCard />
          </div>

          <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-600 text-center pt-2">
            Auto-refreshing every 60s · Data via DHAN
          </div>
        </>
      )}
    </div>
  );
}
